// RisographPerspectiveLenses — Tier-A "Risograph Assembly" renderer for the
// perspective-lenses component.
//
// A React port of the "Risograph Assembly" design-exploration mockup
// (design-explorations/scenario-themes/risograph-assembly/index.html), driven by
// the REAL PerspectiveLensesSlideData + the same state machine as
// PerspectiveLensesSlide's `PerspectiveLensesStudent`. The SIXTH (final) consumer
// of the shared theme-world toolkit (lib/scenario-themes/) — validating it on a
// PRINT / zine register (cream paper, spot-ink misregistration, torn clippings,
// print-shop foley).
//
// State machine + onActivityResponse mirror PerspectiveLensesStudent exactly:
// `activeLensId` + `responses` + `completedLenses` + `synthesis`; each lens lock
// emits the EXACT
//   { activityType:'perspective-lenses', mode:'survey',
//     responseData:{ lensId, answers } }
// payload and advances to the next un-done lens, then the synthesis card.
//
// ★ THE STANDOUT MOMENT ★ — every headline is printed TWICE in two spot inks,
// ~2px out of registration and multiply-blended (a real riso misprint), and
// committing a lens SLAMS a rubber stamp down with an overshoot + an audible
// THUNK. Filing the last view fires the print-roller and the page is stamped FILED.

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { PerspectiveLensesSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { ExperienceClose } from '../../shared/ExperienceClose';

type Lens = PerspectiveLensesSlideData['lenses'][number];

const STYLE_ID = 'riso-pl-scoped-styles';

// Four spot inks (mockup-faithful), assigned per-lens by index.
const INKS = ['#ff48a0', '#2453ff', '#00a878', '#ff6a13'];
const inkOf = (i: number) => INKS[i % INKS.length];
const CREAM = '#f4efe2';
const INK = '#16130d';
const PAPER_SHADOW = 'rgba(22,19,13,.22)';

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced `.riso`; injected once, ref-counted.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(): string {
  return `
.riso { position: relative; --reg: 2px; }
.riso * { box-sizing: border-box; }

/* misregistered spot-ink display type */
.riso .riso-disp { font-family: "Anton", "Archivo Black", sans-serif; text-transform: uppercase; letter-spacing: .5px; line-height: .92; position: relative; display: inline-block; }
.riso .riso-disp[data-ghost]::after { content: attr(data-ghost); position: absolute; left: var(--reg); top: var(--reg); width: 100%; z-index: -1; mix-blend-mode: multiply; color: var(--ghost, ${INK}); pointer-events: none; }
/* ^ width: 100% pins the ghost's wrap width to the original's, so the two copies
   can never line-break differently (an unconstrained absolute ::after had 2px
   less room and could wrap its last word onto a line that stamped over the
   content below). */

.riso .riso-kicker {
  font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 6px; font-size: 13px;
  color: ${INK}; border-top: 3px solid ${INK}; border-bottom: 3px solid ${INK};
  display: block; max-width: 880px; margin: 0 auto; padding: 5px 16px 3px; transform: rotate(-1deg);
}
.riso .riso-edition { font-style: italic; font-size: 14px; opacity: .7; margin-top: 8px; }

/* scenario clipping */
.riso .riso-clip { background: ${CREAM}; border: 2.5px solid ${INK}; box-shadow: 6px 7px 0 ${PAPER_SHADOW}; padding: 26px 30px; position: relative; }
.riso .riso-scenario { transform: rotate(-1.2deg); max-width: 880px; margin: 0 auto 18px; }
.riso .riso-tape { position: absolute; top: -16px; left: 50%; width: 120px; height: 30px; transform: translateX(-50%) rotate(-3deg); background: rgba(255,106,19,.5); border-left: 1px dashed rgba(22,19,13,.4); border-right: 1px dashed rgba(22,19,13,.4); mix-blend-mode: multiply; }
.riso .riso-byline { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 2px; font-size: 12px; color: ${INKS[1]}; margin-bottom: 10px; }
.riso .riso-scenario h1 { margin: 6px 0 14px; font-size: clamp(34px, 6.5vw, 74px); }
.riso .riso-stimulus { font-size: 18px; column-count: 2; column-gap: 34px; margin: 0; line-height: 1.5; }
@media (max-width: 680px) { .riso .riso-stimulus { column-count: 1; } }

/* the exhibit — the artifact every lens re-reads, filed as its own clipping */
.riso .riso-exhibit { transform: rotate(0.8deg); max-width: 780px; margin: 0 auto 18px; }
.riso .riso-exhibit-kind {
  font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 2px;
  font-size: 12px; color: ${INKS[0]}; display: inline-block; margin-bottom: 6px;
  border-bottom: 2px solid ${INKS[0]};
}
.riso .riso-exhibit h2 { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: .5px; font-size: 20px; margin: 0 0 10px; }
.riso .riso-exhibit-body { font-size: 16px; line-height: 1.55; margin: 0; white-space: pre-line; font-family: "Courier Prime", "Courier New", monospace; }
.riso .riso-exhibit-attr { font-style: italic; font-size: 13px; opacity: .65; margin: 10px 0 0; }

/* lens tabs (torn tags) */
.riso .riso-tabs { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; margin: 30px auto 22px; max-width: 920px; }
.riso .riso-tab {
  font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1px; font-size: 16px; cursor: pointer;
  border: 2.5px solid; background: ${CREAM}; padding: 11px 18px 9px; position: relative; box-shadow: 3px 4px 0 ${PAPER_SHADOW};
  transition: transform .12s ease, box-shadow .12s ease;
  clip-path: polygon(0 8%,4% 0,96% 4%,100% 12%,98% 92%,94% 100%,5% 96%,0 88%);
}
.riso .riso-tab:hover { box-shadow: 5px 6px 0 ${PAPER_SHADOW}; }
.riso .riso-tab.active { color: ${CREAM} !important; }
.riso .riso-tab .riso-seen { position: absolute; top: -12px; right: -12px; font-family: "Anton", sans-serif; font-size: 13px; line-height: 1; border: 2.5px solid; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; transform: rotate(-14deg); background: ${CREAM}; }

/* lens panels */
.riso .riso-panel { max-width: 760px; margin: 0 auto; animation: risoSlap .34s cubic-bezier(.2,.9,.3,1.3) both; }
@keyframes risoSlap { 0% { transform: translateY(34px) rotate(-4deg) scale(.95); opacity: 0; } 70% { transform: translateY(-4px) rotate(.6deg) scale(1.01); opacity: 1; } 100% { transform: translateY(0) rotate(-1deg) scale(1); opacity: 1; } }
.riso .riso-panel-card { background: ${CREAM}; border: 3px solid ${INK}; box-shadow: 8px 9px 0 ${PAPER_SHADOW}; padding: 28px 30px 32px; position: relative; }
.riso .riso-staple { position: absolute; top: -7px; left: 28px; width: 34px; height: 14px; border: 2.5px solid ${INK}; border-bottom: none; border-radius: 3px 3px 0 0; transform: rotate(-4deg); }
.riso .riso-staple.two { left: auto; right: 34px; transform: rotate(5deg); }
.riso .riso-who { font-size: clamp(28px, 5vw, 48px); margin: 0 0 4px; }
.riso .riso-rolesub { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 2px; font-size: 12px; margin-bottom: 14px; display: inline-block; padding: 4px 9px 2px; color: ${CREAM}; }
.riso .riso-prompt { font-size: 21px; font-style: italic; line-height: 1.4; margin: 0 0 22px; border-left: 4px solid ${INK}; padding-left: 14px; }
.riso .riso-field { margin-bottom: 18px; }
.riso .riso-field-label { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1.5px; font-size: 12px; display: block; margin: 0 0 6px; }
.riso textarea.riso-input, .riso select.riso-input { width: 100%; font-family: "Newsreader", serif; font-size: 17px; background: rgba(255,255,255,.5); border: 2.5px solid; color: ${INK}; padding: 11px 13px; resize: vertical; }
.riso textarea.riso-input { min-height: 70px; }
.riso .riso-input:focus { outline: none; box-shadow: 3px 3px 0 currentColor; }
.riso .riso-input:disabled { opacity: .7; }
.riso .riso-commit-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-top: 6px; }
.riso .riso-stamp-btn {
  font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1.5px; font-size: 17px; cursor: pointer; color: ${CREAM};
  border: 3px solid ${INK}; padding: 12px 22px 10px; box-shadow: 4px 5px 0 ${INK}; transition: transform .08s ease, box-shadow .08s ease;
}
.riso .riso-stamp-btn:hover:not(:disabled) { transform: translate(-1px,-1px); box-shadow: 6px 7px 0 ${INK}; }
.riso .riso-stamp-btn:active:not(:disabled) { transform: translate(2px,3px); box-shadow: 1px 1px 0 ${INK}; }
.riso .riso-stamp-btn:disabled { opacity: .45; cursor: not-allowed; }
.riso .riso-mark { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1px; font-size: 15px; border: 3px solid currentColor; padding: 6px 12px 4px; transform: rotate(-7deg) scale(0); opacity: 0; transition: transform .3s cubic-bezier(.2,.9,.3,1.6), opacity .2s ease; }
.riso .riso-mark.stamped { transform: rotate(-7deg) scale(1); opacity: 1; }

.riso .riso-progress { text-align: center; margin: 6px auto 0; font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 2px; font-size: 13px; }
.riso .riso-progress b { color: ${INKS[3]}; }

/* synthesis */
.riso .riso-synthesis { max-width: 900px; margin: 48px auto 0; animation: risoFile .5s cubic-bezier(.2,.9,.3,1.2) both; }
@keyframes risoFile { 0% { transform: rotate(.6deg) translateY(40px) scale(.96); opacity: 0; } 100% { transform: rotate(.6deg) translateY(0) scale(1); opacity: 1; } }
.riso .riso-synthesis-card { background: ${CREAM}; border: 4px solid ${INK}; box-shadow: 10px 12px 0 ${INK}; padding: 34px 34px 40px; position: relative; }
.riso .riso-filed { position: absolute; top: 18px; right: 24px; font-family: "Anton", sans-serif; font-size: 20px; color: ${INKS[0]}; border: 4px solid ${INKS[0]}; border-radius: 8px; padding: 8px 12px 5px; transform: rotate(-12deg); mix-blend-mode: multiply; letter-spacing: 1px; }
.riso .riso-synthesis h2 { font-size: clamp(34px, 7vw, 80px); margin: 4px 0 6px; text-align: center; }
.riso .riso-stance-row { display: flex; flex-wrap: wrap; gap: 14px; justify-content: center; margin: 26px 0; }
.riso .riso-stance-chip { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: .5px; font-size: 14px; border: 3px solid currentColor; padding: 9px 13px 7px; text-align: center; background: ${CREAM}; }
.riso .riso-stance-chip small { display: block; font-size: 11px; opacity: .65; letter-spacing: 1px; }

/* prose pull-quotes, the learner's own written answers clipped back at synthesis */
.riso .riso-quote-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 16px; margin: 0 0 26px; }
.riso .riso-quote { background: rgba(255,255,255,.55); border: 2.5px solid; box-shadow: 4px 5px 0 ${PAPER_SHADOW}; padding: 14px 16px 12px; margin: 0; }
.riso .riso-quote-label { font-family: "Anton", sans-serif; text-transform: uppercase; letter-spacing: 1.5px; font-size: 11px; display: block; margin-bottom: 6px; }
.riso .riso-quote p { font-family: "Newsreader", serif; font-style: italic; font-size: 15px; line-height: 1.45; margin: 0; color: ${INK}; }

.riso .riso-reflect { font-size: 22px; font-style: italic; text-align: center; line-height: 1.4; border-top: 3px solid ${INK}; padding-top: 22px; margin-top: 8px; }

/* authored close, shared ExperienceClose content inside print-rule chrome */
.riso .riso-close { max-width: 900px; margin: 44px auto 0; animation: risoFile .5s cubic-bezier(.2,.9,.3,1.2) both; }
.riso .riso-close .riso-kicker { margin-bottom: 18px; transform: rotate(1deg); }

@media (prefers-reduced-motion: reduce) {
  .riso .riso-panel, .riso .riso-synthesis, .riso .riso-close { animation: none !important; }
  .riso .riso-mark { transition-duration: .001s !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// Misregistered spot-ink headline.
function RisoHeadline({ text, ghost, style }: { text: string; ghost: string; style?: React.CSSProperties }) {
  return (
    <span className="riso-disp" data-ghost={text} style={{ ['--ghost' as string]: ghost, ...style }}>
      {text}
    </span>
  );
}

const TAB_ROTATIONS = ['-2.5deg', '1.5deg', '-1deg', '2.5deg'];

// Prose pull-quote excerpting: the first ~140 characters of the learner's own
// writing, cut at a word boundary with an ellipsis; short answers pass whole.
const EXCERPT_LIMIT = 140;
function excerpt(text: string): string {
  const t = text.trim();
  if (t.length <= EXCERPT_LIMIT) return t;
  const cut = t.slice(0, EXCERPT_LIMIT);
  const lastSpace = cut.lastIndexOf(' ');
  // Cut at the last word boundary unless it lands absurdly early.
  return `${(lastSpace > EXCERPT_LIMIT * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

// A sub-prompt renders as prose (textarea) unless it is a select WITH options;
// mirrors the render-time fallback so the payoff matches what the learner typed.
const isProsePrompt = (p: Lens['subPrompts'][number]) =>
  !(p.inputType === 'select' && (p.options?.length ?? 0) > 0);

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function RisographPerspectiveLenses({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: PerspectiveLensesSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();
  useScopedStyles();
  const { enabled: audioOn, toggle: toggleAudio, play } = useThemeWorldAudio('risograph');

  // Per-lens payloads can't reconstruct mid-activity progress; the synthesis
  // payload (lensId === 'synthesis') is the only one that marks the activity as
  // done. Restore-as-fully-done when it's present; otherwise start fresh.
  // (Mirrors PerspectiveLensesStudent's restore policy.)
  const restoredDone = savedResponse?.lensId === 'synthesis';

  // ── State machine (mirrors PerspectiveLensesStudent) ─────────────────────────
  const [activeLensId, setActiveLensId] = useState<string | null>(restoredDone ? null : data.lenses[0]?.id ?? null);
  const [responses, setResponses] = useState<Record<string, Record<string, string>>>({});
  const [completedLenses, setCompletedLenses] = useState<Set<string>>(() =>
    restoredDone ? new Set(data.lenses.map((l) => l.id)) : new Set(),
  );
  const [seenLenses, setSeenLenses] = useState<Set<string>>(() => new Set(data.lenses[0] ? [data.lenses[0].id] : []));
  const [synthesis, setSynthesis] = useState(() => {
    const answers =
      restoredDone && savedResponse && typeof savedResponse.answers === 'object' && savedResponse.answers !== null
        ? (savedResponse.answers as Record<string, unknown>)
        : null;
    return typeof answers?.text === 'string' ? answers.text : '';
  });
  const [synthesisSubmitted, setSynthesisSubmitted] = useState(restoredDone);
  // Fire onComplete exactly once. When the author set NO synthesis prompt the
  // terminal point is the LAST lens being stamped; when a synthesis prompt IS
  // authored the synthesis answer must be captured, so completion waits for the
  // synthesis submit (mirrors PerspectiveLensesStudent). Pre-seed the guard when
  // restoring a saved completion so it never re-fires.
  const completedRef = useRef(restoredDone);

  const markComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete?.();
  }, [onComplete]);

  const allDone = completedLenses.size === data.lenses.length && data.lenses.length > 0;
  const lensIndex = useMemo(() => new Map(data.lenses.map((l, i) => [l.id, i])), [data.lenses]);

  const updateAnswer = (lensId: string, promptId: string, value: string) =>
    setResponses((prev) => ({ ...prev, [lensId]: { ...(prev[lensId] ?? {}), [promptId]: value } }));

  const isLensReady = (lensId: string) => {
    const lens = data.lenses.find((l) => l.id === lensId);
    if (!lens) return false;
    const answers = responses[lensId] ?? {};
    return lens.subPrompts.every((p) => (answers[p.id] ?? '').trim().length > 0);
  };

  const openLens = (lensId: string) => {
    setActiveLensId(lensId);
    setSeenLenses((prev) => (prev.has(lensId) ? prev : new Set(prev).add(lensId)));
    play('select'); // paper rustle
  };

  const rollerFiredRef = useRef(false);
  const completeLens = useCallback(
    (lensId: string) => {
      if (!isLensReady(lensId) || completedLenses.has(lensId)) return;
      const answers = responses[lensId] ?? {};
      onActivityResponse?.({
        activityType: 'perspective-lenses',
        mode: 'survey',
        responseData: { lensId, answers },
      });
      play('commit'); // rubber-stamp thunk
      setCompletedLenses((prev) => {
        const next = new Set(prev).add(lensId);
        const willBeDone = next.size === data.lenses.length;
        if (willBeDone && !rollerFiredRef.current) {
          rollerFiredRef.current = true;
          const delay = reduceMotion ? 0 : 320;
          window.setTimeout(() => play('reveal'), delay); // print-roller ka-chunk
        }
        // Terminal completion point when NO synthesis prompt was authored:
        // stamping the LAST lens completes the activity. When a synthesis prompt
        // exists, completion instead waits for the synthesis submit so the
        // learner's synthesis answer is captured. Fire once via the guard.
        if (willBeDone && !data.synthesis?.prompt) markComplete();
        return next;
      });
      const nextLens = data.lenses.find((l) => l.id !== lensId && !completedLenses.has(l.id));
      if (nextLens) setActiveLensId(nextLens.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [responses, onActivityResponse, data.lenses, data.synthesis, completedLenses, play, reduceMotion, markComplete],
  );

  const submitSynthesis = useCallback(() => {
    if (synthesis.trim().length === 0) return;
    setSynthesisSubmitted(true);
    play('commit'); // rubber-stamp thunk
    if (!reduceMotion) window.setTimeout(() => play('reveal'), 320); // print-roller ka-chunk
    // responseToActivityKey reads lensId, so this lands as its own keyed row.
    onActivityResponse?.({
      activityType: 'perspective-lenses',
      mode: 'survey',
      responseData: { lensId: 'synthesis', answers: { text: synthesis.trim() } },
    });
    markComplete();
  }, [synthesis, onActivityResponse, markComplete, play, reduceMotion]);

  const activeLens: Lens | undefined = data.lenses.find((l) => l.id === activeLensId);
  const activeIdx = activeLens ? lensIndex.get(activeLens.id) ?? 0 : 0;
  const activeInk = inkOf(activeIdx);
  // "examined" = any lens you've opened OR stamped (stamping auto-advances).
  const seenCount = new Set([...seenLenses, ...completedLenses]).size;
  const doneCount = completedLenses.size;

  // Pick a representative "stance" per lens for the synthesis chips (first select answer).
  const stanceFor = (lens: Lens): string => {
    const sel = lens.subPrompts.find((p) => p.inputType === 'select');
    const ans = sel ? responses[lens.id]?.[sel.id] : undefined;
    return (ans && ans.trim()) || 'Filed';
  };

  // Prose payoff (§8.10 gap): only select answers got stance chips, so the
  // learner's WRITTEN answers vanished at synthesis. Excerpt each lens's prose
  // answers into a clipped pull-quote so their own writing visibly feeds the
  // verdict. Lenses with no prose (or a rehydrated done state, where responses
  // are empty) simply contribute no quote.
  const proseQuotes = data.lenses
    .map((lens, i) => ({
      id: lens.id,
      label: lens.label,
      ink: inkOf(i),
      text: excerpt(
        lens.subPrompts
          .filter(isProsePrompt)
          .map((p) => responses[lens.id]?.[p.id])
          .filter((v): v is string => Boolean(v && v.trim()))
          .map((v) => v.trim())
          .join(' · '),
      ),
    }))
    .filter((q) => q.text.length > 0);

  // Fully done = every lens stamped AND (synthesis filed, or none authored);
  // mirrors PerspectiveLensesStudent's `fullyDone` gate for the authored close.
  const fullyDone = allDone && (!data.synthesis?.prompt || synthesisSubmitted);

  // Close echoes: one labelled read per lens, values joined, exactly as the
  // standard skin builds `lensEchoes` (Tier-1 echo type). Labels avoid dashes
  // (echo labels are not run through normalizeProse).
  const lensEchoes = data.lenses.map((lens) => ({
    label: lens.label,
    value: lens.subPrompts
      .map((p) => responses[lens.id]?.[p.id])
      .filter((v): v is string => Boolean(v && v.trim()))
      .join(' · '),
  }));

  return (
    <div
      className="riso h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: INK, fontFamily: theme.fonts.body }}
    >
      <ThemeWorldAtmosphere worldId="risograph" primary={INKS[1]} accent={INKS[0]} background={theme.colors.background} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 2 }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '84px 22px 110px' }}>
          {/* masthead */}
          <header style={{ textAlign: 'center', marginBottom: 6 }}>
            <span className="riso-kicker">The Assembly Press — Special Pull-Out</span>
            <div className="riso-edition">Printed in four spot inks · Press once, decide forever</div>
          </header>

          {/* scenario clipping */}
          <article className="riso-clip riso-scenario">
            <span className="riso-tape" />
            <div className="riso-byline">Front Page · Civic Affairs</div>
            <h1>
              <RisoHeadline text={data.title} ghost={INKS[0]} style={{ color: INK }} />
            </h1>
            {data.subtitle && <p style={{ fontStyle: 'italic', opacity: 0.8, marginTop: -6, marginBottom: 12 }}>{data.subtitle}</p>}
            <p className="riso-stimulus">{data.stimulus.context}</p>
          </article>

          {/* the exhibit — the artifact under examination, filed as its own
              typewritten clipping so every lens question points at something
              actually on the page */}
          {data.exhibit && (
            <article className="riso-clip riso-exhibit">
              <span className="riso-tape" />
              <span className="riso-exhibit-kind">{data.exhibit.kind}</span>
              {data.exhibit.title && <h2>{data.exhibit.title}</h2>}
              <p className="riso-exhibit-body">{data.exhibit.body}</p>
              {data.exhibit.attribution && <p className="riso-exhibit-attr">{data.exhibit.attribution}</p>}
            </article>
          )}

          {/* lens tabs */}
          <nav className="riso-tabs" aria-label="Perspective lenses">
            {data.lenses.map((lens, i) => {
              const ink = inkOf(i);
              const isActive = lens.id === activeLensId;
              const isDone = completedLenses.has(lens.id);
              const isSeen = seenLenses.has(lens.id);
              return (
                <button
                  key={lens.id}
                  type="button"
                  className={`riso-tab${isActive ? ' active' : ''}`}
                  style={{
                    borderColor: ink,
                    color: ink,
                    background: isActive ? ink : CREAM,
                    transform: `rotate(${TAB_ROTATIONS[i % TAB_ROTATIONS.length]})`,
                  }}
                  onClick={() => openLens(lens.id)}
                  onMouseEnter={() => play('hover')}
                >
                  {lens.label}
                  {(isDone || isSeen) && (
                    <span className="riso-seen" style={{ color: isDone ? INKS[2] : INK, borderColor: isDone ? INKS[2] : INK }}>
                      {isDone ? '✓' : '·'}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="riso-progress">
            Lenses examined: <b>{seenCount}</b> / {data.lenses.length} &nbsp;·&nbsp; Views stamped: <b>{doneCount}</b> / {data.lenses.length}
          </div>

          {/* active lens panel */}
          {activeLens && (
            <section className="riso-panel" key={activeLens.id} style={{ marginTop: 22 }}>
              <div className="riso-panel-card">
                <span className="riso-staple" />
                <span className="riso-staple two" />
                <h3 className="riso-who">
                  <RisoHeadline text={activeLens.label} ghost={activeInk} style={{ color: INK }} />
                </h3>
                <span className="riso-rolesub" style={{ background: activeInk }}>
                  Lens {activeIdx + 1} of {data.lenses.length} · {['Pink', 'Blue', 'Green', 'Orange'][activeIdx % 4]} Ink
                </span>
                <p className="riso-prompt">{activeLens.prompt}</p>

                {activeLens.subPrompts.map((p) => {
                  const val = responses[activeLens.id]?.[p.id] ?? '';
                  const disabled = completedLenses.has(activeLens.id);
                  return (
                    <div className="riso-field" key={p.id}>
                      <label className="riso-field-label" style={{ color: activeInk }}>{p.text}</label>
                      {/* A select with missing/empty options is un-answerable —
                          fall back to a text input so the lens can be completed. */}
                      {p.inputType === 'select' && (p.options?.length ?? 0) > 0 ? (
                        <select
                          className="riso-input"
                          style={{ borderColor: activeInk }}
                          value={val}
                          disabled={disabled}
                          onChange={(e) => updateAnswer(activeLens.id, p.id, e.target.value)}
                        >
                          <option value="">— choose a stance —</option>
                          {(p.options ?? []).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <textarea
                          className="riso-input"
                          style={{ borderColor: activeInk }}
                          value={val}
                          disabled={disabled}
                          maxLength={p.maxLength ?? 200}
                          placeholder="Step into their shoes…"
                          onChange={(e) => updateAnswer(activeLens.id, p.id, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}

                <div className="riso-commit-row">
                  {!completedLenses.has(activeLens.id) ? (
                    <button
                      type="button"
                      className="riso-stamp-btn"
                      style={{ background: activeInk }}
                      disabled={!isLensReady(activeLens.id)}
                      onClick={() => completeLens(activeLens.id)}
                    >
                      Stamp this view
                    </button>
                  ) : (
                    <span className="riso-mark stamped" style={{ color: activeInk }}>✓ Stamped</span>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* synthesis — the learner's synthesis answer is CAPTURED on submit
              (fires onActivityResponse with lensId:'synthesis' and completes the
              activity). The "Filed" stamp shows only once filed. */}
          {allDone && data.synthesis?.prompt && (
            <section className="riso-synthesis">
              <div className="riso-synthesis-card">
                {synthesisSubmitted && <div className="riso-filed">Filed</div>}
                <h2>
                  <RisoHeadline text="Many Views, One Road" ghost={INKS[3]} style={{ color: INKS[1] }} />
                </h2>
                {data.synthesis.showLensComparison && (
                  <>
                    <div className="riso-stance-row">
                      {data.lenses.map((lens, i) => (
                        <div key={lens.id} className="riso-stance-chip" style={{ color: inkOf(i), transform: `rotate(${TAB_ROTATIONS[i % 4]})` }}>
                          {stanceFor(lens).toUpperCase()}
                          <small>{lens.label}</small>
                        </div>
                      ))}
                    </div>
                    {/* the learner's prose answers, excerpted as clipped pull-quotes,
                        so their own writing feeds the verdict alongside the chips */}
                    {proseQuotes.length > 0 && (
                      <div className="riso-quote-row">
                        {proseQuotes.map((q, i) => (
                          <figure key={q.id} className="riso-quote" style={{ borderColor: q.ink, transform: `rotate(${i % 2 ? '0.6deg' : '-0.8deg'})` }}>
                            <figcaption className="riso-quote-label" style={{ color: q.ink }}>{q.label} · in their words</figcaption>
                            <p>“{q.text}”</p>
                          </figure>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <p className="riso-reflect">{data.synthesis.prompt}</p>
                <div style={{ marginTop: 16 }}>
                  <textarea
                    className="riso-input"
                    style={{ borderColor: INK }}
                    value={synthesis}
                    rows={4}
                    disabled={synthesisSubmitted}
                    placeholder="Write it here — there's no wrong answer, only an honest one."
                    onChange={(e) => setSynthesis(e.target.value)}
                  />
                </div>
                <div className="riso-commit-row" style={{ marginTop: 14 }}>
                  {!synthesisSubmitted ? (
                    <button
                      type="button"
                      className="riso-stamp-btn"
                      style={{ background: INKS[3] }}
                      disabled={synthesis.trim().length === 0}
                      onClick={submitSynthesis}
                    >
                      File the verdict
                    </button>
                  ) : (
                    <span className="riso-mark stamped" style={{ color: INKS[2] }}>✓ Filed — view recorded</span>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Authored close (§8.1): once the verdict is filed (or the last lens is
              stamped when no synthesis is authored), the shared ExperienceClose
              lands the "So what?", echoing the learner's reads per lens, exactly
              as the standard skin does. Content comes from the shared component;
              only the wrapper chrome is risograph. No authored conclusion means
              no close (back-compat). */}
          {fullyDone && data.conclusion && (
            <section className="riso-close">
              <div style={{ textAlign: 'center' }}>
                <span className="riso-kicker">The Editor's Note · Final Column</span>
              </div>
              <ExperienceClose conclusion={data.conclusion} theme={theme} echoes={lensEchoes} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
